import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { OnChangeFn, OnTouchedFn, provideCva } from '../cva.utils';

export type CheckboxSize = 'sm' | 'md';

@Component({
  selector: 'app-checkbox',
  standalone: true,
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideCva(CheckboxComponent)],
  host: {
    '[class.app-checkbox-disabled]': 'isDisabled()',
    '[class.app-checkbox-checked]': 'isChecked()',
    '[class.app-checkbox-indeterminate]': 'isIndeterminate()',
    '[class.app-checkbox-readonly]': 'readonly()',
    '[class.app-checkbox-size-md]': 'size() === "md"',
    '[class.app-checkbox-has-label]': '!!label()',
    '(click)': 'onHostClick($event)',
  },
})
export class CheckboxComponent implements ControlValueAccessor {
  readonly label = input<string>('');
  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly size = input<CheckboxSize>('sm');
  readonly checked = input<boolean | undefined>(undefined);
  readonly indeterminate = input(false);

  readonly checkedChange = output<boolean>();

  private readonly cvaValue = signal<boolean | null>(null);
  private readonly disabledFromForm = signal(false);

  readonly isChecked = computed(() => {
    const fromCva = this.cvaValue();
    if (fromCva !== null) {
      return fromCva;
    }
    return this.checked() ?? false;
  });

  readonly isDisabled = computed(() => this.disabled() || this.disabledFromForm());

  readonly isIndeterminate = computed(() => this.indeterminate() && !this.isChecked());

  private onChange: OnChangeFn<boolean> = () => {};
  private onTouched: OnTouchedFn = () => {};

  writeValue(value: boolean | null): void {
    this.cvaValue.set(value == null ? false : !!value);
  }

  registerOnChange(fn: OnChangeFn<boolean>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledFromForm.set(isDisabled);
  }

  onHostClick(event: Event): void {
    if (this.isDisabled() || this.readonly()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  private toggle(): void {
    const next = !this.isChecked();
    if (this.cvaValue() !== null) {
      this.cvaValue.set(next);
      this.onChange(next);
      this.onTouched();
    }
    this.checkedChange.emit(next);
  }
}
